import { ApolloClient, from, InMemoryCache, defaultDataIdFromObject, ApolloLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { HttpLink } from "@apollo/client/link/http";
import { ErrorResponse, onError } from "@apollo/client/link/error";
import { navigate } from "@reach/router";
import logger from "services/logger";
import { userLoginToken, wipeToken } from "utils/general-utils";
import possibleTypes from "./possible-types.json";

const dynamicUriLink = new ApolloLink((operation, forward) => {
  const { operationName } = operation;
  const customUri = `/graphql/${operationName}`;

  // Attach the URI to the context so HttpLink can use it
  operation.setContext(({ headers = {} }) => ({
    uri: customUri,
    headers,
  }));

  return forward(operation);
});

type NetworkError = {
  statusCode: number
}

const setAuthorizationHeaders = setContext(() => {
  const token = userLoginToken();
  return token === null ? { headers: {} } : { headers: { authorization: `Bearer ${token}` } };
});

const errorLink = onError(({ graphQLErrors, networkError }: ErrorResponse) => {
  if (graphQLErrors) {
    console.error("[GraphQL error] type:", typeof graphQLErrors);
    if (graphQLErrors instanceof Array) {
      graphQLErrors.forEach((error) => {
        console.error("[GraphQL error]", error);
        return logger.sendError(error as Error);
      });
    }
  }

  if (networkError !== null) {
    logger.sendError(networkError as Error);

    if (((networkError as unknown as NetworkError)?.statusCode ?? 0) === 403) {
      wipeToken();
      navigate("/login");
    }
  }
});

const httpLink = new HttpLink({ uri: "/graphql" });

const logLink = new ApolloLink((operation, forward) => {
  try {

    // Extract query details
    const operationData = {
      query: operation.query.loc?.source.body,
      variables: operation.variables,
      operationName: operation.operationName,
    };

    // Fetch existing logs from sessionStorage, or initialize an empty array if none exist
    const existingLogs = JSON.parse(sessionStorage.getItem("apolloQueryLogs") ?? "[]");

    // Add the new operation data to the logs
    existingLogs.push(operationData);

    // Save updated logs back to sessionStorage
    sessionStorage.setItem("apolloQueryLogs", JSON.stringify(existingLogs));

  } catch (error) {
    console.error(error);
  }
  return forward(operation);
});

export const apolloClient = new ApolloClient({
  link: from([dynamicUriLink, logLink, setAuthorizationHeaders, errorLink, httpLink]),
  cache: new InMemoryCache({
    possibleTypes,
    dataIdFromObject (responseObject) {
      switch (responseObject.__typename) {
        case "WaitlistUpdate": return responseObject.interestList ? `WaitlistUpdate:${(responseObject.interestList as any)?.id}` : defaultDataIdFromObject(responseObject);
        default: return defaultDataIdFromObject(responseObject);
      }
    },
  }),
});
